/*
 * <license header>
 */

import {
  Content,
  Heading,
  IllustratedMessage,
  Provider as SpectrumProvider,
  ButtonGroup,
  Button,
} from "@react-spectrum/s2";
import Server from "@react-spectrum/s2/illustrations/linear/Server";
import { ErrorBoundary } from "react-error-boundary";
import { useStore } from "zustand";
import { useNavigate, Routes, Route } from "react-router";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";

import { store } from "../store";
import { queryClient, asyncStoragePersister } from "../store/query";

import { Layout } from "./Layout";
import { View } from "./view/View";

import { style } from "@react-spectrum/s2/style" with { type: "macro" };
import "@react-spectrum/s2/page.css";

function App() {
  const locale = useStore(store, (state) => state.locale);
  const colorScheme = useStore(store, (state) => state.colorScheme);

  const navigate = useNavigate();

  return (
    <ErrorBoundary onError={onError} FallbackComponent={fallbackComponent}>
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{
          persister: asyncStoragePersister,
          // Restored mutations have no mutationFn and fail when resumed.
          dehydrateOptions: { shouldDehydrateMutation: () => false },
        }}
      >
        <SpectrumProvider
          colorScheme={colorScheme}
          locale={locale}
          router={{ navigate }}
          background="layer-1"
        >
          <Routes>
            {/*
             * `/view/:viewId` renders outside the default app layout so each
             * view can define its own main layout. The trailing splat lets a
             * view own its nested routing under `/view/:viewId/*`.
             */}
            <Route path="/view/:viewId/*" element={<View />} />

            {/* Everything else renders inside the default app layout. */}
            <Route path="/*" element={<Layout />} />
          </Routes>
        </SpectrumProvider>
      </PersistQueryClientProvider>
    </ErrorBoundary>
  );

  // Methods

  // error handler on UI rendering failure
  function onError(e, componentStack) {}

  // component to show if UI fails rendering
  function fallbackComponent({ error, resetErrorBoundary }) {
    console.warn(error);
    return (
      <div
        className={style({
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "[100vw]",
          height: "[100vh]",
        })}
      >
        <IllustratedMessage>
          <Server />
          <Heading>Oops, something went wrong</Heading>
          <Content>
            <div className={style({ font: "code-sm" })}>
              {error?.message ?? "But no worries. Let's give it another go."}
            </div>
          </Content>
          <ButtonGroup>
            <Button variant="primary" onPress={resetErrorBoundary}>
              Try again
            </Button>
          </ButtonGroup>
        </IllustratedMessage>
      </div>
    );
  }
}

export default App;
